import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError } from './lib/api'
import { ThemeProvider, ToastProvider } from './components/ui'
import { App } from './App'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Board data is invalidated by live-sync events, so polling on focus
      // would only duplicate work the event stream already does.
      refetchOnWindowFocus: false,
      staleTime: 10_000,
      retry: (failureCount, error) => {
        // A rejected request is an answer, not a blip: retrying a 403 or a
        // validation failure just delays showing the user what went wrong.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      },
    },
  },
})

/**
 * Dropping a file anywhere the app does not handle makes the browser navigate
 * away and open it, losing whatever was on screen. Since the whole app is one
 * page, that reads as "attachments are broken" — you aimed at a task, missed,
 * and lost your place. Swallowing stray drops at the document level makes a
 * miss do nothing instead.
 */
for (const type of ['dragover', 'drop'] as const) {
  window.addEventListener(type, (e) => {
    if (!(e as DragEvent).dataTransfer?.types.includes('Files')) return
    if ((e.target as HTMLElement | null)?.closest('[data-file-drop]')) return
    e.preventDefault()
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
